import React, { useCallback, useState } from 'react';
import { IconButton, Paper, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { GroupTypeDetails } from 'graphql/types';
import { useMessage, useMessages } from 'modules/intl';
import { useFeatureFlag } from 'modules/common/hooks';
import { SimpleDropDown } from './SimpleDropDown';
import { GroupSelectClassification } from './GroupSelectClassification';
import { EnabledDisabled } from './constant';
import { SimpleRemoveDialogBox } from './SimpleRemoveDialogBox';

const useStyles = makeStyles((theme) => ({
  paperContent: {
    display: 'flex',
    borderRadius: theme.spacing(0, 1.25, 0, 0),
    flexDirection: 'column',
    alignContent: 'flex-start',
    margin: theme.spacing(1.5, 2)
  },
  span: {
    display: 'flex'
  },
  dropDown: {
    marginRight: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between'
  },
  secondaryActionDialog: {
    display: 'flex',
    alignContent: 'flex-start',
    flexWrap: 'wrap',
    width: '100%',
    padding: theme.spacing(1)
  },
  text: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0)
  },

  removeButton: {
    alignSelf: 'center'
  }
}));

const alertMessage = [
  'settings.removeDialog.title',
  'settings.removeDialog.Remove',
  'settings.removeDialog.Cancel',
  'settings.removeDialog.dialogText1',
  'settings.removeDialog.dialogText2'
];

type GroupSelectionRowProps = {
  options: {
    key: EnabledDisabled;
    value: string;
  }[];
  enabledGroupTypes: GroupTypeDetails[] | undefined | null;
  onRemove: (rowIndex: number) => void;
  rowIndex: number;
  rowID: string;
  field: Record<string, any>;
  label?: string;
};
export const GroupSelectionRow: React.FC<GroupSelectionRowProps> = ({
  options,
  enabledGroupTypes,
  onRemove,
  rowIndex,
  rowID,
  field,
  label
}: GroupSelectionRowProps) => {
  const classes = useStyles();
  const alertDialogBoxMessages = useMessages(alertMessage);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  const handleDialogOpen = useCallback(() => {
    setRemoveDialogOpen(true);
  }, []);
  const handleDialogClose = useCallback(() => {
    setRemoveDialogOpen(false);
  }, []);
  const handleRemove = useCallback(() => {
    onRemove(rowIndex);
  }, [onRemove, rowIndex]);
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const removeButtonMsg = useMessage('settings.remove');

  return (
    <Paper
      className={classes.paperContent}
      data-testid={`setting-content-layout-${rowIndex}`}
      elevation={3}
    >
      <div className={classes.dropDown}>
        <span className={classes.span}>
          <SimpleDropDown
            options={options}
            rowID={rowID}
            value={field.enableDisabled}
            label={label}
          ></SimpleDropDown>
          <Typography
            display="inline"
            className={classes.text}
            variant={'caption'}
          >
            <FormattedMessage id="settings.usersIn" />
          </Typography>
        </span>
        <div className={classes.removeButton}>
          <IconButton
            color="secondary"
            onClick={handleDialogOpen}
            data-testid={`remove-button-${rowID}`}
            size="large"
            aria-label={isTiwScreenReaderEnabled ? removeButtonMsg : ''}
          >
            <RemoveCircleOutlineOutlinedIcon />
          </IconButton>
          <SimpleRemoveDialogBox
            removeDialogOpen={removeDialogOpen}
            handleRemove={handleRemove}
            handleDialogClose={handleDialogClose}
            title={alertDialogBoxMessages['settings.removeDialog.title']}
            contentText={[
              alertDialogBoxMessages['settings.removeDialog.dialogText1'],
              alertDialogBoxMessages['settings.removeDialog.dialogText2']
            ]}
            textButton={alertDialogBoxMessages['settings.removeDialog.Remove']}
            containedButton={
              alertDialogBoxMessages['settings.removeDialog.Cancel']
            }
            icon={<RemoveCircleOutlineOutlinedIcon />}
          />
        </div>
      </div>
      <div className={classes.secondaryActionDialog}>
        {enabledGroupTypes &&
          enabledGroupTypes.map(({ value }) => (
            <GroupSelectClassification
              groupType={value}
              key={value}
              rowID={rowID}
              defaultValues={field[value]}
            />
          ))}
      </div>
    </Paper>
  );
};
